import { useState, useEffect, useMemo } from 'react';
import { Button, Modal, Card, Form, OverlayTrigger, Popover } from 'react-bootstrap';
import { Trash, Plus, ArrowRight, Gear, InfoCircle } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import {
  runSimulation,
} from '../../../../../utils/simulationRule';
import { ProgressionRule, SimulationResult } from '../../../../../types/tournament';

const SimulationVisualiser = ({ result }: { result: SimulationResult | null }) => {
  if (!result) return null;

  return (
    <div className="row g-3" style={{ minHeight: '400px' }}>
      <div className="col-md-6 d-flex flex-column">
        <div className="bg-light p-3 rounded border h-100">
          <h6 className="text-uppercase text-muted font-weight-bold mb-3 small">
            Current Round (Mock)
          </h6>
          <div className="row g-2">
            {Object.entries(result.mockStandings).map(([groupId, players]) => (
              <div key={groupId} className="col-12 col-xl-6">
                <div className="bg-white border rounded p-2 shadow-sm h-100">
                  <div className="d-flex justify-content-between border-bottom pb-1 mb-1">
                    <span className="small font-weight-bold text-secondary">
                      Box {Number(groupId) + 1}
                    </span>
                  </div>
                  {players.map((p) => (
                    <div
                      key={p.userId}
                      className={`d-flex justify-content-between small px-1 rounded ${
                        p.isAllocated ? 'bg-success-subtle text-dark' : 'text-muted'
                      }`}
                      style={{ fontSize: '0.8rem' }}
                    >
                      <span className="text-truncate" style={{ maxWidth: '70%' }}>
                        <span className="font-weight-bold">#{p.rank}</span> Player {p.userId}
                      </span>
                      {p.isAllocated && (
                        <span
                          className="text-success font-weight-bold ml-1"
                          style={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                        >
                          ➜ {p.destinationGroup}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="col-md-1 d-flex align-items-center justify-content-center text-secondary">
        <ArrowRight size={20} />
      </div>

      <div className="col-md-5 d-flex flex-column">
        <div
          className="p-3 rounded border border-primary bg-opacity-10 h-100"
          style={{ backgroundColor: '#f0f8ff' }}
        >
          <h6 className="text-uppercase text-primary font-weight-bold mb-3 small">Final round</h6>
          <div className="d-flex flex-column gap-3">
            {result.targetGroups.map((group, idx) => (
              <div key={idx} className="bg-white border border-primary rounded p-2 shadow-sm">
                <div className="d-flex justify-content-between align-items-center mb-2 border-bottom border-primary pb-1">
                  <span
                    className="font-weight-bold text-primary text-truncate small"
                    style={{ maxWidth: '70%' }}
                  >
                    {group.groupName}
                  </span>
                  <span className="badge bg-primary" style={{ fontSize: '0.7em' }}>
                    {group.players.length} Players
                  </span>
                </div>
                {group.players.length === 0 ? (
                  <div className="text-center text-muted small fst-italic py-1">
                    No players qualified
                  </div>
                ) : (
                  <div className="row g-1">
                    {group.players.map((p) => (
                      <div key={p.userId} className="col-6">
                        <div className="border rounded bg-light px-2 py-1 small h-100">
                          <span
                            className="d-block font-weight-bold text-truncate"
                            style={{ fontSize: '0.8rem' }}
                          >
                            Player {p.userId}
                          </span>
                          <span
                            className="d-block text-muted text-truncate"
                            style={{ fontSize: '0.65em' }}
                          >
                            Box {p.groupId + 1} (#{p.rank})
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {result.targetGroups.length === 0 && (
              <div className="text-center text-muted mt-5 small">Add a rule to see progression</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface Props {
  numberOfGroups: number;
  defaultPlayers: number;
  existingRules?: ProgressionRule[];
  onRulesChange: (rules: ProgressionRule[]) => void;
}

export const ProgressionRuleBuilderModal = ({
  numberOfGroups,
  defaultPlayers,
  existingRules = [],
  onRulesChange,
}: Props) => {
  const { t } = useTranslation();
  const sortPrefix = 'creativeDetail-progressionSortBy';
  const [show, setShow] = useState(false);

  const [numPlayers, setNumPlayers] = useState(defaultPlayers);

  const [rules, setRules] = useState<ProgressionRule[]>(existingRules);
  const [simResult, setSimResult] = useState<SimulationResult | null>(null);

  useEffect(() => {
    if (show) {
      setRules(existingRules);
      //setNumPlayers(defaultPlayers);
    }
  }, [show, existingRules, defaultPlayers]);

  // Rerun simulation if changes
  useEffect(() => {
    const safeGroups = Math.max(1, numberOfGroups);
    const safePlayers = Math.max(2, numPlayers);
    setSimResult(runSimulation(safeGroups, safePlayers, rules));
  }, [numberOfGroups, numPlayers, rules]);

  // Update local state and parent
  const updateRules = (newRules: ProgressionRule[]) => {
    setRules(newRules);
    onRulesChange(newRules);
  };

  const addRule = () => {
    updateRules([
      ...rules,
      {
        targetGroupName: `Final Box ${rules.length + 1}`,
        sources: [{ sourceGroupIds: [], positions: [], limit: 0, sortBy: 'stats' }],
      },
    ]);
  };

  const updateRuleName = (idx: number, name: string) => {
    const newRules = [...rules];
    newRules[idx] = {
      ...newRules[idx],
      targetGroupName: name,
    };
    updateRules(newRules);
  };

  const removeRule = (idx: number) => {
    updateRules(rules.filter((_, i) => i !== idx));
  };

  const addSource = (ruleIdx: number) => {
    const newRules = [...rules];
    const updatedRule = { ...newRules[ruleIdx] };
    updatedRule.sources = [...updatedRule.sources, { sourceGroupIds: [], positions: [], limit: 0, sortBy: 'stats' }];
    newRules[ruleIdx] = updatedRule;
    updateRules(newRules);
  };

  const updateSource = (ruleIdx: number, sourceIdx: number, field: string, value: any) => {
    const newRules = [...rules];
    const updatedRule = { ...newRules[ruleIdx] };
    const updatedSources = [...updatedRule.sources];

    updatedSources[sourceIdx] = {
      ...updatedSources[sourceIdx],
      [field]: value,
    };

    updatedRule.sources = updatedSources;
    newRules[ruleIdx] = updatedRule;

    updateRules(newRules);
  };

  const removeSource = (ruleIdx: number, sourceIdx: number) => {
    const newRules = [...rules];
    const updatedRule = { ...newRules[ruleIdx] };
    updatedRule.sources = updatedRule.sources.filter((_, i) => i !== sourceIdx);
    newRules[ruleIdx] = updatedRule;
    updateRules(newRules);
  };

  const toggleArrayItem = (arr: number[], item: number) => {
    return arr.includes(item)
      ? arr.filter((i) => i !== item)
      : [...arr, item].sort((a, b) => a - b);
  };

  const sortByInfoPopover = (
    <Popover id="progression-sort-by-info" style={{ maxWidth: '320px' }}>
      <Popover.Header as="h6">{t(sortPrefix + 'InfoTitle', 'How Sort By works')}</Popover.Header>
      <Popover.Body className="small">
        <p className="mb-2">
          {t(
            sortPrefix + 'InfoIntro',
            'Players are always taken by finishing position first (1st before 2nd, and so on). Sort By only decides between players with the same position in different boxes.'
          )}
        </p>
        <p className="mb-1">
          <strong>{t(sortPrefix + 'Stats', 'Stats')}:</strong>{' '}
          {t(sortPrefix + 'InfoStats', 'the best-performing player is taken first.')}
        </p>
        <p className="mb-2">
          <strong>{t(sortPrefix + 'BoxOrder', 'Box order')}:</strong>{' '}
          {t(
            sortPrefix + 'InfoBoxOrder',
            'the player from the lowest-numbered box is taken first, regardless of results.'
          )}
        </p>
        <p className="mb-1 text-muted">
          {t(
            sortPrefix + 'InfoExample',
            'Example: take 1 of the 2nd-placed players. Box 1 (3 wins), Box 2 (5 wins), Box 3 (4 wins).'
          )}
        </p>
        <ul className="mb-0 pl-3 ps-3">
          <li>{t(sortPrefix + 'InfoExampleStats', 'Stats picks Box 2.')}</li>
          <li>{t(sortPrefix + 'InfoExampleBoxOrder', 'Box order picks Box 1.')}</li>
        </ul>
      </Popover.Body>
    </Popover>
  );

  const availableGroups = useMemo(
    () => Array.from({ length: numberOfGroups }, (_, i) => i + 1),
    [numberOfGroups]
  );

  const maxGroupSize = Math.ceil(Math.max(2, numPlayers) / Math.max(1, numberOfGroups));
  const availablePositions = useMemo(
    () => Array.from({ length: maxGroupSize }, (_, i) => i + 1),
    [maxGroupSize]
  );

  return (
    <>
      <div className="d-flex align-items-center gap-3">
        <Button
          variant="outline-primary"
          onClick={() => setShow(true)}
          className="d-flex align-items-center gap-2"
        >
          <Gear /> Configure Progression Rules
        </Button>
        <div className="text-muted small">
          {rules.length === 0
            ? 'No rules defined.'
            : `${rules.length} Target Box rule${rules.length !== 1 ? 's' : ''} active`}
        </div>
      </div>

      <Modal show={show} onHide={() => setShow(false)} size="xl" centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Progression rules creator</Modal.Title>
        </Modal.Header>

        <Modal.Body className="bg-light">
          <div className="bg-white p-3 rounded shadow-sm mb-3 d-flex flex-wrap align-items-center gap-4 border">
            <div className="d-flex align-items-center gap-2">
              <strong className="small text-uppercase text-muted">Simulation Context:</strong>
              <span className="badge bg-info text-dark border">{numberOfGroups} Boxes</span>
            </div>

            <Form.Group className="d-flex">
              <Form.Label className="small font-weight-bold text-muted mb-0">
                Test with Players:
              </Form.Label>
              <Form.Control
                type="number"
                value={numPlayers}
                onChange={(e) => setNumPlayers(Number(e.target.value))}
                min={2}
              />
            </Form.Group>

            <div className="small text-muted ms-auto">
              Changes are saved automatically
            </div>
          </div>

          <div className="row">
            <div className="col-lg-4 mb-4 mb-lg-0">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="font-weight-bold m-0 text-secondary">Rules Definition</h6>
                <Button
                  variant="dark"
                  size="sm"
                  onClick={addRule}
                  className="d-flex align-items-center gap-1 py-0"
                >
                  <Plus /> Add Target Box
                </Button>
              </div>

              <div className="d-flex flex-column gap-3">
                {rules.map((rule, ruleIndex) => (
                  <Card key={ruleIndex} className="border-secondary shadow-sm">
                    <Card.Header className="bg-white d-flex justify-content-between align-items-center py-2 px-3">
                      <div className="d-flex align-items-center gap-2 w-100">
                        <span className="text-muted small">Name:</span>
                        <input
                          type="text"
                          className="form-control form-control-sm font-weight-bold border-0 p-0 h-auto text-primary"
                          value={rule.targetGroupName}
                          onChange={(e) => updateRuleName(ruleIndex, e.target.value)}
                          style={{ maxWidth: '200px', fontSize: '1rem', background: 'transparent' }}
                        />
                      </div>
                      <Button
                        variant="link"
                        className="text-danger p-0"
                        onClick={() => removeRule(ruleIndex)}
                        title="Remove Rule"
                      >
                        <Trash size={14} />
                      </Button>
                    </Card.Header>
                    <Card.Body className="p-2 bg-light">
                      {rule.sources.map((source, sourceIndex) => (
                        <div
                          key={sourceIndex}
                          className="bg-white border rounded p-2 mb-2 position-relative"
                        >
                          <div
                            className="position-absolute"
                            style={{ top: '5px', right: '5px', zIndex: 10 }}
                          >
                            <button
                              type="button"
                              className="btn-close"
                              style={{ fontSize: '0.6rem' }}
                              aria-label="Close"
                              onClick={() => removeSource(ruleIndex, sourceIndex)}
                            ></button>
                          </div>

                          <h6 className="small font-weight-bold text-secondary text-uppercase mb-2">
                            Source #{sourceIndex + 1}
                          </h6>

                          <div className="mb-2">
                            <label className="d-block small text-muted mb-1">From Boxes:</label>
                            <div className="d-flex flex-wrap gap-1">
                              <button
                                type="button"
                                className={`btn btn-sm py-0 px-2 small ${
                                  source.sourceGroupIds.length === availableGroups.length
                                    ? 'btn-primary'
                                    : 'btn-outline-primary'
                                }`}
                                onClick={() =>
                                  updateSource(
                                    ruleIndex,
                                    sourceIndex,
                                    'sourceGroupIds',
                                    source.sourceGroupIds.length === availableGroups.length
                                      ? []
                                      : [...availableGroups]
                                  )
                                }
                                style={{ fontSize: '0.7rem' }}
                              >
                                All
                              </button>
                              {availableGroups.map((gId) => (
                                <button
                                  type="button"
                                  key={gId}
                                  className={`btn btn-sm py-0 px-2 small ${
                                    source.sourceGroupIds.includes(gId)
                                      ? 'btn-primary'
                                      : 'btn-outline-secondary'
                                  }`}
                                  onClick={() =>
                                    updateSource(
                                      ruleIndex,
                                      sourceIndex,
                                      'sourceGroupIds',
                                      toggleArrayItem(source.sourceGroupIds, gId)
                                    )
                                  }
                                  style={{ fontSize: '0.7rem' }}
                                >
                                  {gId}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="mb-2">
                            <label className="d-block small text-muted mb-1">
                              Finishing Position (Rank):
                            </label>
                            <div className="d-flex flex-wrap gap-1">
                              {availablePositions.map((pos) => (
                                <button
                                  type="button"
                                  key={pos}
                                  className={`rounded-circle btn btn-sm p-0 d-flex align-items-center justify-content-center ${
                                    source.positions.includes(pos)
                                      ? 'btn-dark'
                                      : 'btn-outline-secondary'
                                  }`}
                                  style={{ width: '22px', height: '22px', fontSize: '0.7rem' }}
                                  onClick={() =>
                                    updateSource(
                                      ruleIndex,
                                      sourceIndex,
                                      'positions',
                                      toggleArrayItem(source.positions, pos)
                                    )
                                  }
                                >
                                  {pos}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="row g-2 align-items-end">
                            <div className="col-6">
                              <label className="small text-muted d-block">
                                Limit (Max Players)
                              </label>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                placeholder="All"
                                value={source.limit || ''}
                                onChange={(e) =>
                                  updateSource(
                                    ruleIndex,
                                    sourceIndex,
                                    'limit',
                                    Number(e.target.value)
                                  )
                                }
                              />
                            </div>
                            <div className="col-6">
                              <label className="small text-muted d-block">
                                {t(sortPrefix + 'Label', 'Sort By')}
                                <OverlayTrigger
                                  placement="auto"
                                  trigger={['hover', 'focus']}
                                  overlay={sortByInfoPopover}
                                >
                                  <Button
                                    variant="link"
                                    className="p-0 ml-1 ms-1 align-baseline"
                                    aria-label={t(sortPrefix + 'InfoTitle', 'How Sort By works')}
                                  >
                                    <InfoCircle size={12} aria-hidden="true" />
                                  </Button>
                                </OverlayTrigger>
                              </label>
                              <select
                                className="form-select form-select-sm"
                                value={source.sortBy || 'stats'}
                                onChange={(e) =>
                                  updateSource(ruleIndex, sourceIndex, 'sortBy', e.target.value)
                                }
                              >
                                <option value="stats">{t(sortPrefix + 'Stats', 'Stats')}</option>
                                <option value="position">
                                  {t(sortPrefix + 'BoxOrder', 'Box order')}
                                </option>
                              </select>
                            </div>
                          </div>
                          <div className="small text-muted mt-1">
                            {(source.sortBy || 'stats') === 'position'
                              ? t(
                                  sortPrefix + 'BoxOrderHelp',
                                  'Lower-numbered box first, regardless of results.'
                                )
                              : t(
                                  sortPrefix + 'StatsHelp',
                                  'Best results first, even if the player is in a higher-numbered box.'
                                )}
                          </div>
                        </div>
                      ))}
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="w-100 dashed-border small"
                        style={{ borderStyle: 'dashed' }}
                        onClick={() => addSource(ruleIndex)}
                      >
                        + Add Source Rule
                      </Button>
                    </Card.Body>
                  </Card>
                ))}

                {rules.length === 0 && (
                  <div className="text-center p-4 border border-dashed rounded text-muted bg-white">
                    <p className="mb-0">No Rules defined.</p>
                    <p className="small">Click "Add Target Box" to start.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="col-lg-8">
              <div className="h-100 border-start ps-lg-3">
                <h6 className="font-weight-bold text-secondary mb-1">Projection</h6>
                <p className="small text-muted mb-3">
                  Based on {numberOfGroups} boxes and {numPlayers} test players.{' '}
                  {t('creativeDetail-progressionPreviewNote', 'The preview uses made-up results.')}
                </p>
                <SimulationVisualiser result={simResult} />
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};
